import type { Editor } from "@tiptap/react";
import type { LottieRefCurrentProps } from "lottie-react";
import type { ChatMessage as ChatMessageType } from "schemas/generated/chatMessage";

import React, { useRef, useState } from "react";

import LogoBadge from "@svg/logo-badge";
import SvgMessages from "@svg/messages";

import { Button } from "@replo/design-system/components/button/Button";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import { toast } from "@replo/design-system/components/toast/Toast";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { CircleCheckIcon } from "@replo/design-system/icons/animated/CircleCheckIcon";
import { CopyIcon } from "@replo/design-system/icons/animated/CopyIcon";
import twMerge from "@replo/design-system/utils/twMerge";
import { ChatMessageListContext } from "@v2/features/chat/components/Chat";
import { ImageMessage } from "@v2/features/chat/components/chat-elements/chat-message/ImageMessageItem";
import { TextMessage } from "@v2/features/chat/components/chat-elements/chat-message/TextMessageItem";
import { IconWrapper } from "@v2/features/chat/components/chat-elements/IconWrapper";
import MessageGroup from "@v2/features/chat/components/chat-elements/message-group/MessageGroup";
import {
  PAGE_PLACEHOLDER_OPTIONS,
  PLACEHOLDER_OPTIONS,
} from "@v2/features/chat/constants/placeholderOptions";
import { useFindLastEditedPageIdFromMessages } from "@v2/features/chat/hooks/useFindLastEditedPageIdFromMessages";
import { useIsChatLoading } from "@v2/features/chat/store/ChatStoreContext";
import { useErrorToast } from "@v2/hooks/useErrorToast";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { writeHtmlToClipboard } from "@v2/utils/clipboard";
import Lottie from "lottie-react";
import { Dot } from "lucide-react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useRequiredContext } from "replo-utils/react/context";

type MessageGroupType = {
  role: "user" | "assistant";
  messages: ChatMessageType[];
};

/**
 * Groups consecutive chat messages by role for easier rendering.
 *
 * This function transforms a flat array of individual messages into grouped message objects.
 * User messages remain grouped as "user", while both "assistant" and "tool" messages are
 * grouped together under the "assistant" role since they represent the AI's response flow.
 *
 * @param messages - Array of individual chat messages from the database
 * @returns Array of message groups where consecutive messages of the same role type are combined
 *
 * @example
 * Input:
 * [
 *   { role: 'user', content: [] },
 *   { role: 'assistant', content: [] },
 *   { role: 'tool', content: [] },
 *   { role: 'assistant', content: [] }
 * ]
 *
 * Output:
 * [
 *   { role: "user", messages: [{ role: 'user', content: [] }] },
 *   {
 *     role: "assistant",
 *     messages: [
 *       { role: "assistant", content: [] },
 *       { role: "tool", content: [] },
 *       { role: "assistant", content: [] }
 *     ]
 *   }
 * ]
 */
const groupMessages = (messages: ChatMessageType[]): MessageGroupType[] => {
  return messages.reduce<MessageGroupType[]>((groups, message) => {
    // this makes everything not a user an assistant message (e.g. tool messages).
    const groupRole = message.role === "user" ? "user" : "assistant";
    const lastGroup = groups[groups.length - 1];

    // Check if any content item has isNewGroup flag
    const shouldStartNewGroup = message.content.some((contentItem) => {
      return (
        contentItem.type === "text" && contentItem.isNewMessageGroup === true
      );
    });

    if (lastGroup?.role === groupRole && !shouldStartNewGroup) {
      lastGroup.messages.push(message);
    } else {
      groups.push({
        role: groupRole,
        messages: [message],
      });
    }

    return groups;
  }, []);
};

const noopAudio = {
  play: () => {},
  pause: () => {},
  seek: () => {},
  playing: () => {},
  rate: () => {},
  setVolume: () => {},
};

const PromptCardIcon = ({
  animationData,
  iconClassName,
  lottieClassName,
  isHovered,
  onLoad,
}: {
  animationData: unknown;
  iconClassName?: string;
  lottieClassName?: string;
  isHovered: boolean;
  onLoad?: () => void;
}) => {
  const lottieRef = useRef<LottieRefCurrentProps | null>(null);
  const wasHoveredRef = useRef(false);

  React.useEffect(() => {
    if (isHovered && !wasHoveredRef.current) {
      lottieRef.current?.play();
    } else if (!isHovered && wasHoveredRef.current) {
      lottieRef.current?.stop();
    }
    wasHoveredRef.current = isHovered;
  }, [isHovered]);

  return (
    <div
      className={twMerge(
        "mix-blend-multiply w-16 h-16 -mb-3 overflow-hidden",
        iconClassName,
      )}
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        className={twMerge("w-16 h-16 -ml-1 -mt-1", lottieClassName)}
        autoplay={false}
        loop={false}
        onDOMLoaded={onLoad}
        audioFactory={() => noopAudio}
      />
    </div>
  );
};

type PlaceholderOption =
  | {
      animationData: unknown;
      iconClassName?: string;
      lottieClassName?: string;
      title: string;
      subtitle: string;
      prompt: string;
      action?: string;
    }
  | {
      icon: React.ReactNode;
      title: string;
      subtitle: string;
      prompt: string;
      action?: string;
    };

const PlaceholderOptionCard = ({
  option,
  onClick,
  onLottieLoad,
}: {
  option: PlaceholderOption;
  onClick: () => void;
  onLottieLoad?: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const hasAnimationData = "animationData" in option;

  return (
    <div
      className="flex flex-col border border-border rounded-[10px] p-3 hover:bg-sheet cursor-pointer select-text relative transition-colors"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="mb-2">
        {hasAnimationData ? (
          <PromptCardIcon
            animationData={option.animationData}
            iconClassName={option.iconClassName}
            lottieClassName={option.lottieClassName}
            isHovered={isHovered}
            onLoad={onLottieLoad}
          />
        ) : (
          <IconWrapper size="base">{option.icon}</IconWrapper>
        )}
      </div>
      <div className="text-sm font-medium leading-5 text-default">
        {option.title}
      </div>
      <div className="text-xs leading-4 text-placeholder">
        {option.subtitle}
      </div>
    </div>
  );
};

const PlaceholderView = ({
  editor,
  onQuickPrompt,
}: {
  editor: Editor | null;
  onQuickPrompt?: (prompt: string, action?: string) => void;
}) => {
  const [searchParams] = useSearchParams();
  const logEvent = useLogAnalytics();
  const pageId = searchParams.get("pageId");
  const currentPlaceholderOptions: PlaceholderOption[] = pageId
    ? PAGE_PLACEHOLDER_OPTIONS
    : PLACEHOLDER_OPTIONS;

  const promptIconCount = currentPlaceholderOptions.filter(
    (opt) => "animationData" in opt,
  ).length;
  const [loadedIconCount, setLoadedIconCount] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  // Fallback timeout - show cards after 5s even if icons haven't loaded
  React.useEffect(() => {
    const timeout = setTimeout(() => setTimedOut(true), 5000);
    return () => clearTimeout(timeout);
  }, []);

  const isLoading =
    promptIconCount > 0 && loadedIconCount < promptIconCount && !timedOut;

  return (
    <div className="flex flex-col h-full w-full min-h-0 items-center justify-center relative pb-28">
      {isLoading ? (
        <Spinner className="size-8" />
      ) : (
        <>
          <SvgMessages className="h-12 w-auto hidden md:block" />
          <div className="header-h4 mt-1">Perfect your page</div>
          <div className="text-xs text-muted mb-7">
            Some quick actions to get you started
          </div>
        </>
      )}
      {/* Always render cards so Lotties can load, hide while loading */}
      <div
        className={twMerge(
          "grid grid-cols-2 gap-3 relative",
          isLoading && "invisible absolute",
        )}
      >
        {currentPlaceholderOptions.map((option, index) => (
          <PlaceholderOptionCard
            key={index}
            option={option}
            onLottieLoad={() => setLoadedIconCount((c) => c + 1)}
            onClick={() => {
              logEvent("chat.selectRecommendationPrompt", {
                origin: "chat",
                prompt: option.prompt,
              });
              if (onQuickPrompt) {
                onQuickPrompt(option.prompt, option.action);
              } else {
                editor?.commands.setContent(option.prompt);
              }
            }}
          />
        ))}
      </div>
    </div>
  );
};

const AssistantTypingIndicator = () => {
  return (
    <div className="flex flex-row items-center justify-start w-full gap-1">
      <LogoBadge className="w-6 h-6 shrink-0" aria-hidden />

      <div className="flex -space-x-2.5">
        <Dot className="h-5 w-5 animate-typing-dot-bounce" />
        <Dot className="h-5 w-5 animate-typing-dot-bounce [animation-delay:90ms]" />
        <Dot className="h-5 w-5 animate-typing-dot-bounce [animation-delay:180ms]" />
      </div>
    </div>
  );
};

const CopyMessageButton = ({ html }: { html: string }) => {
  const [isCopied, setIsCopied] = useState(false);
  const errorToast = useErrorToast();

  const handleCopy = async () => {
    await writeHtmlToClipboard(html, errorToast);

    setIsCopied(true);
    toast({ title: "Message copied to clipboard" });

    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  return (
    <Tooltip content="Copy message">
      <Button
        icon
        variant="tertiary"
        size="sm"
        onClick={() => void handleCopy()}
      >
        {isCopied ? (
          <CircleCheckIcon className="text-emerald" />
        ) : (
          <CopyIcon className="text-muted" />
        )}
      </Button>
    </Tooltip>
  );
};

const PendingMessageView = ({
  pendingMessage,
}: {
  pendingMessage: { text: string; files?: File[] };
}) => {
  return (
    <div className="gap-3 w-full flex flex-col items-end">
      {pendingMessage.files && pendingMessage.files.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {pendingMessage.files
            .filter((f) => f instanceof Blob && f.type?.startsWith("image/"))
            .map((file, idx) => (
              <ImageMessage
                key={`image-${idx}`}
                src={URL.createObjectURL(file)}
              />
            ))}
        </div>
      )}

      <TextMessage text={pendingMessage.text} role="user" />
      <CopyMessageButton html={pendingMessage.text} />

      <AssistantTypingIndicator />
    </div>
  );
};

interface MessageListProps {
  editor: Editor | null;
  isTyping?: boolean;
  onQuickPrompt?: (prompt: string, action?: string) => void;
}

export function MessageList({
  isTyping = false,
  editor,
  onQuickPrompt,
}: MessageListProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");
  const location = useLocation();

  const isChatLoading = useIsChatLoading(chatId);
  const { messages } = useRequiredContext(ChatMessageListContext);

  // NOTE (Ben, 2025-12-05): Capture pending message on mount via useState initializer.
  // This runs BEFORE ReactEditorChat clears location.state via window.history.replaceState.
  const [pendingMessage, setPendingMessage] = React.useState<{
    text: string;
    files?: File[];
  } | null>(() => {
    const initialData = location.state as {
      initialMessage?: string;
      initialFiles?: File[] | null;
    } | null;

    if (initialData?.initialMessage) {
      return {
        text: initialData.initialMessage,
        files: initialData.initialFiles ?? undefined,
      };
    }
    return null;
  });

  const groupedMessages = groupMessages(messages);

  const lastEditedPageId = useFindLastEditedPageIdFromMessages(messages);

  // NOTE (Bobby, 2025-07-30): Auto-sets pageId URL param when switching chats based on last edited page.
  // Uses ref to track chat:page combinations to prevent overriding manual page switches and handle
  // trpc query timing (lastEditedPageId loads after chatId changes).
  const lastProcessedRef = React.useRef<string | null>(null);

  /**
   * Updates search params to set or remove pageId based on lastEditedPageId
   */
  const updatePageIdParams = React.useCallback(
    (lastEditedPageId: string | null) => {
      setSearchParams((prev) => {
        if (lastEditedPageId && prev.get("pageId") !== lastEditedPageId) {
          // Set pageId if there's a lastEditedPageId
          if (prev.get("pageId") !== lastEditedPageId) {
            const newParams = new URLSearchParams(prev);
            newParams.set("pageId", lastEditedPageId);
            return newParams;
          }
        } else if (
          !lastEditedPageId &&
          // NOTE (Bobby, 2025-08-18): Initial user messages sent through are "synthetic" messages and not actually from the db. This condition is to make sure
          // we don't clear the pageId from the initial sent messages.
          messages.some((m) => m.role === "assistant" || m.role === "tool") &&
          prev.has("pageId")
        ) {
          // Remove pageId if there's no lastEditedPageId
          const newParams = new URLSearchParams(prev);
          newParams.delete("pageId");
          return newParams;
        }
        return prev;
      });
    },
    [setSearchParams, messages],
  );

  /**
   * Handles the chat page update logic with deduplication
   */
  const handleChatPageUpdate = React.useCallback(
    (chatId: string, lastEditedPageId: string | null) => {
      const currentCombination = `${chatId}:${lastEditedPageId ?? "none"}`;
      // Only update if we haven't processed this combination before
      if (lastProcessedRef.current !== currentCombination) {
        lastProcessedRef.current = currentCombination;
        updatePageIdParams(lastEditedPageId);
      }
    },
    [updatePageIdParams],
  );

  React.useEffect(() => {
    if (chatId) {
      handleChatPageUpdate(chatId, lastEditedPageId);
    }
  }, [chatId, lastEditedPageId, handleChatPageUpdate]);

  React.useEffect(() => {
    if (pendingMessage && messages.length > 1) {
      setPendingMessage(null);
    }
  }, [pendingMessage, messages]);

  const shouldShowPlaceholder =
    messages.length === 0 && !isChatLoading && !chatId && !pendingMessage;
  const shouldShowPendingMessage = pendingMessage && messages.length === 0;

  return (
    <div
      className={twMerge(
        "space-y-0 flex flex-col gap-3 w-full max-w-[880px] mx-auto min-h-0",
        shouldShowPlaceholder && "h-full justify-center",
      )}
    >
      {shouldShowPendingMessage && (
        <PendingMessageView pendingMessage={pendingMessage} />
      )}

      {shouldShowPlaceholder && (
        <PlaceholderView editor={editor} onQuickPrompt={onQuickPrompt} />
      )}

      {groupedMessages.map((group, index) => {
        const isLastAssistantGroup =
          group.role === "assistant" && index === groupedMessages.length - 1;

        return (
          <MessageGroup
            key={index}
            role={group.role}
            messages={group.messages}
            isChatLoading={isChatLoading}
            isLastAssistantGroup={isLastAssistantGroup}
          />
        );
      })}

      {isTyping && <AssistantTypingIndicator />}
    </div>
  );
}
